/*
 * Liquid-glass refraction in raw WebGL.
 *
 * A full-cover <canvas> sits over the page background. The shader treats a
 * rounded-rect region (the card) as a glass slab: it builds a bezel height
 * field from a rounded-rect SDF, refracts the background texture through it
 * (Snell's law / IOR), and adds specular rim, inner shadow and tint. Outside
 * the slab the output is transparent so the real DOM background shows through.
 *
 * Works in any WebGL-capable webview (Chromium + WebKit), so unlike
 * `backdrop-filter: url()` it also runs in Mac Excel.
 */

export interface LiquidGlassParams {
  radius: number
  bezel: number
  thickness: number
  ior: number
  blur: number
  specular: number
  tint: number
  shadow: number
}

export interface LiquidGlassRect {
  x: number
  y: number
  w: number
  h: number
}

export interface LiquidGlassHandle {
  destroy(): void
}

const VERT = `
attribute vec2 aPos;
attribute vec2 aUv;
varying vec2 vUv;
void main() {
  vUv = aUv;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`

// Fragment shader (uniform plumbing matched to raw WebGL).
const FRAG = `
precision highp float;
varying vec2 vUv;

uniform vec2 uResolution;
uniform vec2 uGlassCenter;
uniform vec2 uGlassSize;
uniform float uRadius;
uniform float uBezel;
uniform float uThickness;
uniform float uIOR;
uniform float uBlur;
uniform float uSpecular;
uniform float uTint;
uniform float uShadow;
uniform sampler2D uBgTex;
uniform float uBgAspect;

float sdRoundedRect(vec2 p, vec2 halfSize, float r) {
  vec2 q = abs(p) - halfSize + r;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
}

float surfaceHeight(float t) {
  float s = 1.0 - t;
  return pow(1.0 - s*s*s*s, 0.25);
}

vec3 sampleBg(vec2 screenUV) {
  float screenAspect = uResolution.x / uResolution.y;
  vec2 uv = screenUV;
  if (uBgAspect > screenAspect) {
    float s = screenAspect / uBgAspect;
    uv.x = uv.x * s + (1.0 - s) * 0.5;
  } else {
    float s = uBgAspect / screenAspect;
    uv.y = uv.y * s + (1.0 - s) * 0.5;
  }
  uv.y = 1.0 - uv.y;
  return texture2D(uBgTex, uv).rgb;
}

vec3 sampleBgBlurred(vec2 uv, float radius) {
  if (radius < 0.5) return sampleBg(uv);
  vec3 sum = vec3(0.0);
  vec2 px = 1.0 / uResolution;
  vec2 offsets[16];
  offsets[0]  = vec2(-0.94201, -0.39906);
  offsets[1]  = vec2( 0.94558, -0.76890);
  offsets[2]  = vec2(-0.09418, -0.92938);
  offsets[3]  = vec2( 0.34495,  0.29387);
  offsets[4]  = vec2(-0.91588, -0.45771);
  offsets[5]  = vec2(-0.81544,  0.48568);
  offsets[6]  = vec2(-0.38277, -0.56071);
  offsets[7]  = vec2(-0.12675,  0.84686);
  offsets[8]  = vec2( 0.89642,  0.41254);
  offsets[9]  = vec2( 0.18150, -0.30020);
  offsets[10] = vec2(-0.01445, -0.16001);
  offsets[11] = vec2( 0.59614,  0.71118);
  offsets[12] = vec2( 0.49742, -0.47280);
  offsets[13] = vec2( 0.80685,  0.04588);
  offsets[14] = vec2(-0.32490, -0.03965);
  offsets[15] = vec2(-0.60975,  0.06566);
  for (int i = 0; i < 16; i++) {
    sum += sampleBg(uv + offsets[i] * radius * px);
  }
  return sum / 16.0;
}

void main() {
  vec2 screenPx = vec2(vUv.x, 1.0 - vUv.y) * uResolution;
  vec2 p = screenPx - uGlassCenter;
  vec2 halfSize = uGlassSize * 0.5;

  float sd = sdRoundedRect(p, halfSize, uRadius);

  if (sd > 0.0) {
    float shadowFalloff = exp(-sd * sd / 800.0);
    float shadowAlpha = uShadow * shadowFalloff * 0.6;
    gl_FragColor = vec4(0.0, 0.0, 0.0, shadowAlpha);
    return;
  }

  float distFromEdge = -sd;
  float maxBezel = min(uRadius, min(halfSize.x, halfSize.y)) - 1.0;
  float bezel = max(1.0, min(uBezel, maxBezel));
  float t = clamp(distFromEdge / bezel, 0.0, 1.0);

  float h = surfaceHeight(t);
  float dt = 0.001;
  float h2 = surfaceHeight(min(t + dt, 1.0));
  float dh = (h2 - h) / dt;

  float slopeAngle = atan(dh * (uThickness / bezel));
  float sinR = sin(slopeAngle) / uIOR;
  sinR = clamp(sinR, -1.0, 1.0);
  float thetaR = asin(sinR);
  float displacement = h * uThickness * (tan(slopeAngle) - tan(thetaR));

  vec2 grad;
  float eps = 0.5;
  grad.x = sdRoundedRect(p + vec2(eps, 0.0), halfSize, uRadius) - sd;
  grad.y = sdRoundedRect(p + vec2(0.0, eps), halfSize, uRadius) - sd;
  grad = normalize(grad);

  vec2 offset = -grad * displacement / uResolution;

  vec2 screenUV = screenPx / uResolution;
  vec2 refractedUV = screenUV + offset;

  vec3 color = sampleBgBlurred(refractedUV, uBlur);

  vec2 lightDir = normalize(vec2(0.5, -0.7));
  float rimDot = abs(dot(grad, lightDir));
  float rimFalloff = 1.0 - smoothstep(0.0, bezel * 0.4, distFromEdge);
  float specHighlight = pow(rimDot * rimFalloff, 1.5);
  color += vec3(specHighlight * uSpecular);

  float innerShadow = 1.0 - smoothstep(0.0, bezel * 0.6, distFromEdge);
  color *= mix(1.0, 0.7, innerShadow * 0.3);

  float innerRim = smoothstep(0.0, 2.0, distFromEdge) * (1.0 - smoothstep(2.0, 5.0, distFromEdge));
  color += vec3(innerRim * 0.15 * uSpecular);

  color = mix(color, vec3(1.0), uTint);

  float alpha = smoothstep(0.0, 1.5, distFromEdge);
  gl_FragColor = vec4(color, alpha);
}`

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type)
  if (!sh) return null
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.warn('liquidGlass shader compile failed:', gl.getShaderInfoLog(sh))
    gl.deleteShader(sh)
    return null
  }
  return sh
}

export function createLiquidGlass(
  canvas: HTMLCanvasElement,
  getRect: () => LiquidGlassRect | null,
  drawBackdrop: (ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, elapsedMs: number) => void,
  params: LiquidGlassParams,
): LiquidGlassHandle | null {
  let gl: WebGLRenderingContext | null = null
  try {
    gl =
      (canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false }) as WebGLRenderingContext | null) ||
      (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null)
  } catch {
    gl = null
  }
  if (!gl) return null

  // Free the context on any init failure instead of leaking it until GC.
  const fail = () => {
    try {
      gl?.getExtension('WEBGL_lose_context')?.loseContext()
    } catch {
      /* noop */
    }
    return null
  }

  const vs = compile(gl, gl.VERTEX_SHADER, VERT)
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG)
  if (!vs || !fs) return fail()

  const prog = gl.createProgram()
  if (!prog) return fail()
  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.warn('liquidGlass link failed:', gl.getProgramInfoLog(prog))
    return fail()
  }
  gl.useProgram(prog)

  // Fullscreen quad
  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  // x, y, u, v
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1]),
    gl.STATIC_DRAW,
  )
  const aPos = gl.getAttribLocation(prog, 'aPos')
  const aUv = gl.getAttribLocation(prog, 'aUv')
  gl.enableVertexAttribArray(aPos)
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 16, 0)
  gl.enableVertexAttribArray(aUv)
  gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 16, 8)

  const U = {
    uResolution: gl.getUniformLocation(prog, 'uResolution'),
    uGlassCenter: gl.getUniformLocation(prog, 'uGlassCenter'),
    uGlassSize: gl.getUniformLocation(prog, 'uGlassSize'),
    uRadius: gl.getUniformLocation(prog, 'uRadius'),
    uBezel: gl.getUniformLocation(prog, 'uBezel'),
    uThickness: gl.getUniformLocation(prog, 'uThickness'),
    uIOR: gl.getUniformLocation(prog, 'uIOR'),
    uBlur: gl.getUniformLocation(prog, 'uBlur'),
    uSpecular: gl.getUniformLocation(prog, 'uSpecular'),
    uTint: gl.getUniformLocation(prog, 'uTint'),
    uShadow: gl.getUniformLocation(prog, 'uShadow'),
    uBgTex: gl.getUniformLocation(prog, 'uBgTex'),
    uBgAspect: gl.getUniformLocation(prog, 'uBgAspect'),
  }

  // Background texture, drawn from a 2D canvas (the page's textured backdrop)
  const tex = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, tex)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

  const bgCanvas = document.createElement('canvas')
  const bgCtx = bgCanvas.getContext('2d')
  if (!bgCtx) return fail()
  let texW = 0
  let texH = 0

  function uploadBackdrop(w: number, h: number, dpr: number, elapsedMs: number) {
    if (!gl || !bgCtx) return
    if (w !== texW || h !== texH) {
      bgCanvas.width = w
      bgCanvas.height = h
      texW = w
      texH = h
    }
    drawBackdrop(bgCtx, w, h, dpr, elapsedMs)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    // the ported shader expects a top-left-origin (flipped) texture
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bgCanvas)
  }

  gl.clearColor(0, 0, 0, 0)

  let raf = 0
  let destroyed = false
  let lastW = 0
  let lastH = 0
  const startTime = performance.now()
  let lastBgUpload = -1e9

  function frame() {
    if (destroyed || !gl) return
    const nowT = performance.now()
    const elapsed = nowT - startTime
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const cssW = canvas.clientWidth
    const cssH = canvas.clientHeight
    const w = Math.max(1, Math.round(cssW * dpr))
    const h = Math.max(1, Math.round(cssH * dpr))

    let resized = false
    if (w !== lastW || h !== lastH) {
      canvas.width = w
      canvas.height = h
      gl.viewport(0, 0, w, h)
      lastW = w
      lastH = h
      resized = true
    }
    // redraw the (animated) backdrop texture on resize, else throttled to ~20fps
    if (resized || nowT - lastBgUpload >= 50) {
      uploadBackdrop(w, h, dpr, elapsed)
      lastBgUpload = nowT
    }

    const rect = getRect()
    gl.clear(gl.COLOR_BUFFER_BIT)
    if (rect && rect.w > 2 && rect.h > 2) {
      gl.useProgram(prog)
      gl.uniform2f(U.uResolution, w, h)
      gl.uniform2f(U.uGlassCenter, (rect.x + rect.w / 2) * dpr, (rect.y + rect.h / 2) * dpr)
      gl.uniform2f(U.uGlassSize, rect.w * dpr, rect.h * dpr)
      gl.uniform1f(U.uRadius, params.radius * dpr)
      gl.uniform1f(U.uBezel, params.bezel * dpr)
      gl.uniform1f(U.uThickness, params.thickness * dpr)
      gl.uniform1f(U.uIOR, params.ior)
      gl.uniform1f(U.uBlur, params.blur * dpr)
      gl.uniform1f(U.uSpecular, params.specular)
      gl.uniform1f(U.uTint, params.tint)
      gl.uniform1f(U.uShadow, params.shadow)
      gl.uniform1f(U.uBgAspect, w / h)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, tex)
      gl.uniform1i(U.uBgTex, 0)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }
    raf = requestAnimationFrame(frame)
  }
  raf = requestAnimationFrame(frame)

  return {
    destroy() {
      destroyed = true
      cancelAnimationFrame(raf)
      try {
        gl?.getExtension('WEBGL_lose_context')?.loseContext()
      } catch {
        /* noop */
      }
    },
  }
}
